<template>

    <div class="multiselect" v-click-outside="hideMenu" ref="multiselect">
        <div class="multiselect-input" ref="input" tabindex="0" @click="toggleShow" @keydown="onTriggerKeydown">
            <span v-if="!selectedItems.length" class="multiselect-placeholder">{{ placeholder ? placeholder : $t('labels.select_an_option') }}</span>
            <div v-for="(i, index) in selectedItems" :key="`selected-item-${index}`" class="multiselect-selected-item" :title="i.label">
                <span v-if="i.icon" class="icon" :style="{ 'color': i.color }">
                    <i v-if="i.icon.startsWith('fa')" :class="i.icon" />
                    <img v-else :src="i.icon" width="16" height="16"  />
                </span>
                <span>{{ i.label | truncate(30) }}</span>
                <span class="remove" @click.stop="removeItem(i)">
                    <i class="fas fa-times" />
                </span>
            </div>
        </div>
        <transition name="dropdown">
            <div v-show="show" class="multiselect-menu" :class="{ 'disabled': !items.length, 'multiselect-menu-fixed': fixed }" :style="menuStyle" ref="menu">
                <div v-if="addable" class="multiselect-add">
                    <v-input size="sm" :model="addition" @input="addition = $event" @keydown.native.enter="addItem" :placeholder="$t('labels.add') + '...'" />
                </div>
                <div v-if="showMenuSearch" class="multiselect-search" @click.stop>
                    <v-input
                        class="w-fluid"
                        clearable
                        :clear-aria-label="$t('labels.clear')"
                        :model="query"
                        :placeholder="$t('labels.search') + '...'"
                        ref="searchInput"
                        size="sm"
                        @clear="query = ''"
                        @input="query = $event"
                        @keydown.native.down.prevent="moveHighlight(1)"
                        @keydown.native.up.prevent="moveHighlight(-1)"
                        @keydown.native.enter.prevent="selectHighlighted"
                        @keydown.native.esc.prevent="hideMenu"
                    />
                </div>
                <div v-if="items.length" class="multiselect-items" ref="itemsList">
                    <div v-for="(i, index) in visibleItems" :key="`item-${index}`" :class="itemClasses(i, index)" :title="i.label" @click.stop="selectItem(i)" @mouseenter="highlight = index">
                        <div>
                            <span v-if="i.icon" class="icon" :style="{ 'color': i.color }">
                                <i v-if="i.icon.startsWith('fa')" :class="i.icon" />
                                <img v-else :src="i.icon" width="16" height="16"  />
                            </span>
                            <span>{{ i.label | truncate(50) }}</span>
                        </div>
                        <i v-if="selectedItems.includes(i)" class="fas fa-check" />
                    </div>
                    <div v-if="searchHasNoResults" class="multiselect-item disabled">
                        <span>{{ $t('labels.no_available_options') }}</span>
                    </div>
                </div>
                <div v-else class="multiselect-item disabled">
                    <span>{{ empty ? empty : $t('labels.no_available_options') }}</span>
                </div>
            </div>
        </transition>
    </div>

</template>

<style scoped lang="scss">

    .multiselect {

        flex-shrink: 0;
        position: relative;

        .multiselect-input {
            background: $color-white;
            border: 1px solid $color-rule;
            border-radius: 5px;
            cursor: pointer;
            display: flex;
            flex-wrap: wrap;
            gap: 5px;
            min-height: 50px;
            padding: 9px 10px;
            transition: border-color .3s;
            &:hover {
                border-color: $color-text;
            }
        }

        .multiselect-placeholder {
            @include font-regular(14px);
            color: $color-muted;
            line-height: 30px;
            margin-left: 5px;
            user-select: none;
        }

        .multiselect-selected-item {
            @include font-regular(13px);
            align-items: center;
            background: $color-bg;
            border-radius: 15px;
            color: $color-text;
            display: flex;
            line-height: 30px;
            padding: 0 2px 0 12px;
            user-select: none;
            white-space: nowrap;
            .icon {
                margin-right: 5px;
                text-align: center;
                width: 16px;
            }
            .remove {
                align-items: center;
                color: $color-text;
                display: flex;
                height: 30px;
                justify-content: center;
                opacity: 0.5;
                transition: opacity .3s;
                width: 30px;
                &:hover {
                    opacity: 1;
                }
            }
        }

        .multiselect-menu {
            @include shad(2);
            background: $color-white;
            border: 1px solid $color-rule;
            border-radius: 5px;
            box-sizing: border-box;
            display: block;
            left: 0;
            margin: 5px 0 0 0;
            padding: 15px 0;
            position: absolute;
            z-index: 100;
            &.multiselect-menu-fixed {
                margin: 0;
                z-index: 10002;
            }
        }

        .multiselect-items {
            max-height: 450px;
            overflow-y: auto;
        }

        .multiselect-item {
            @include font-regular(13px);
            align-items: center;
            background-color: $color-white;
            border: none;
            color: $color-text;
            cursor: pointer;
            display: flex;
            height: 30px;
            justify-content: space-between;
            margin: 0;
            padding: 0 15px;
            position: relative;
            text-decoration: none;
            transition: all .3s;
            user-select: none;
            white-space: nowrap;
            &:hover,
            &.highlighted {
                background-color: $color-highlight;
            }
            &.disabled {
                background-color: $color-white !important;
                color: $color-muted;
                cursor: default;
            }
            .icon {
                margin-right: 5px;
                text-align: center;
                width: 16px;
            }
        }

        .multiselect-add {
            padding: 0 10px 10px 10px;
        }

        .multiselect-search {
            border-bottom: 1px solid $color-rule-light;
            padding: 0 10px 10px 10px;
        }

    }

</style>

<script>

import { entityMenuMaxWidth, entityMenuMinWidth, entitySearchMinCount } from '@/config';
import ClickOutside from 'vue-click-outside';

export default {

    directives: {
        ClickOutside,
    },

    props: {
        model: {
            required: true,
        },
        items: {
            type: Array,
            default: () => [],
        },
        placeholder: {
            type: String,
        },
        empty: {
            type: String,
        },
        addable: {
            type: Boolean,
            default: false
        },
        fixed: {
            type: Boolean,
            default: false,
        },
        searchable: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            addition: '',
            highlight: 0,
            query: '',
            menuPosition: { left: 0, top: 0, width: 300 },
            selectedItems: [],
            show: false,
            val: this.model !== null && this.model !== undefined ? this.model : [],
        }
    },

    computed: {

        menuStyle() {
            const min_width = this.fixed
                ? Math.max(entityMenuMinWidth, this.menuPosition.width)
                : entityMenuMinWidth;
            const styles = {
                maxWidth: `${entityMenuMaxWidth}px`,
                minWidth: `${min_width}px`,
            };
            if (!this.fixed) {
                return styles;
            }
            return {
                ...styles,
                left: `${this.menuPosition.left}px`,
                position: 'fixed',
                top: `${this.menuPosition.top}px`,
            };
        },

        showMenuSearch() {
            return this.searchable && this.items.length >= entitySearchMinCount;
        },

        visibleItems() {
            if (!this.showMenuSearch) {
                return this.items;
            }
            const q = this.query.trim().toLowerCase();
            if (!q) {
                return this.items;
            }
            return this.items.filter(i => String(i.label || '').toLowerCase().includes(q));
        },

        searchHasNoResults() {
            if (!this.showMenuSearch || !this.query.trim()) {
                return false;
            }
            return !this.visibleItems.length;
        },

    },

    created() {
        // Sort items.
        this.items = this.sortItems(this.items);
        // Get selected items.
        this.selectedItems = this.items.filter(i => this.model.includes(i.value));
    },

    watch: {

        model(val) {
            this.val = val;
        },

        show(is_visible) {
            if (is_visible && this.fixed) {
                this.$nextTick(() => this.updateMenuPosition());
            }
            if (is_visible) {
                this.highlight = 0;
                this.$nextTick(() => {
                    if (this.showMenuSearch) {
                        this.$refs.searchInput?.focus();
                    } else {
                        this.$refs.input?.focus();
                    }
                });
            }
            if (!is_visible) {
                this.query = '';
                this.highlight = 0;
            }
        },

        query() {
            this.highlight = 0;
        },

        val(val) {
            this.selectedItems = this.items.filter(i => val.includes(i.value));
            this.$emit('input', val);
        },

    },

    methods: {

        itemClasses(item, index) {
            return {
                'multiselect-item': true,
                'highlighted': index === this.highlight,
                'selected': this.selectedItems.includes(item),
            };
        },

        onTriggerKeydown(e) {
            if (!this.show) {
                if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.toggleShow();
                }
                return;
            }
            if (this.showMenuSearch) {
                return;
            }
            this.onMenuKeydown(e);
        },

        onMenuKeydown(e) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                this.moveHighlight(1);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                this.moveHighlight(-1);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                this.selectHighlighted();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                this.hideMenu();
            }
        },

        selectHighlighted() {
            const item = this.visibleItems[this.highlight];
            if (item) {
                this.selectItem(item);
            }
        },

        moveHighlight(step) {
            if (!this.visibleItems.length) {
                return;
            }
            const count = this.visibleItems.length;
            this.highlight = (this.highlight + step + count) % count;
            this.scrollToHighlight();
        },

        scrollToHighlight() {
            this.$nextTick(() => {
                const list = this.$refs.itemsList;
                if (!list) {
                    return;
                }
                const item = list.children[this.highlight];
                if (item) {
                    item.scrollIntoView({ block: 'nearest' });
                }
            });
        },

        selectItem(item, hide_after_select = true) {
            if (!this.selectedItems.includes(item)) {
                this.selectedItems.push(item);
                this.selectedItems = this.sortItems(this.selectedItems);
                this.val = this.selectedItems.map(i => i.value);
                if (hide_after_select) {
                    this.hideMenu();
                }
            } else {
                this.removeItem(item);
            }
        },

        removeItem(item) {
            this.selectedItems = this.selectedItems.filter(i => i.value !== item.value);
            this.selectedItems = this.sortItems(this.selectedItems);
            this.val = this.selectedItems.map(i => i.value);
            this.hideMenu();
        },

        addItem() {
            const value = this.addition.trim();
            if (!value) return;
            if (!this.items.find(i => i.value === value)) {
                const item = { value: value, label: value };
                this.items.push(item);
                this.selectItem(item, false);
            }
            this.addition = '';
        },

        sortItems(items) {
            return items.sort((a, b) => a.label.localeCompare(b.label));
        },

        hideMenu() {
            this.query = '';
            this.highlight = 0;
            this.show = false;
        },

        toggleShow() {
            if (!this.show && this.fixed) {
                this.updateMenuPosition();
            }
            this.show = !this.show;
        },

        updateMenuPosition() {
            if (!this.$refs.input) return;
            const rect = this.$refs.input.getBoundingClientRect();
            this.menuPosition = {
                left: rect.left,
                top: rect.bottom + 5,
                width: rect.width,
            };
        },

    },

}

</script>
